import { type z } from 'zod';
import { Proxify } from '@epihub/core/src/instrument/proxify';
import { GenericError } from '@epihub/core/src/errors';
import { safely } from '@epihub/core/src/fn/safely';
import { decodeBase64 } from '@epihub/core/src/fn/encode';
import { type PackageConst } from '@epihub/core/src/epikit/types';
import { Process } from '@epihub/core/src/process/Process';

export function packageVarsConsumer<Type extends object>(
  opts: {
    parser: z.ZodType<Type>;
    packageConst: PackageConst;
  },
) {
  let config: any = null;
  let configAt: string | null = null;
  return Proxify<Type>({
    get({ prop }) {
      if (prop === '__extractParser') {
        return opts.parser;
      }

      const configIsStale = Process.env.PACKAGE_VARS_STALE
        && configAt
        && configAt < Process.env.PACKAGE_VARS_STALE;

      if (config == null || configIsStale) {
        const unparsedConfig: Record<string, Record<string, any>> | null = safely(
          () => JSON.parse(decodeBase64(Process.env.PACKAGE_VARS)),
          () => null,
        );

        config = !unparsedConfig
          ? null
          : {
            ...unparsedConfig,
            [opts.packageConst.appName]: unparsedConfig[opts.packageConst.appName]
              ? opts.parser.parse(unparsedConfig[opts.packageConst.appName])
              : null,
          };

        configAt = new Date().toISOString();
      }

      return config?.[opts.packageConst.appName]?.[prop];
    },
    set() {
      throw new GenericError({
        scope: 'packageVarsConsumer',
        message: [
          'Setting a DynamicConfig var is not allowed.',
          'Set the env value PACKAGE_VARS instead and then invalidate this config',
        ].join(', '),
      });
    },
  });
}
